// Wires the Helper sync (helperSync.js) into the running app, once, at application level -- not inside any page, so it is
// the same on the dashboard, the lists, quotations, products, settings...: it observes the store and the session, nothing else.
//
//   * sign-in / branch list loaded      -> store watcher (selected branch, its Service Rating mode)
//   * branch switch / setting changed   -> the same watcher (the store's branch list is updated when settings are saved)
//   * sign-out / expired session        -> router (the login route) + the access_token itself, re-read after every navigation
//   * sign-in or sign-out in ANOTHER tab -> the `storage` event
//
// A sign-in that has not loaded its branches yet says nothing (see helperSync.js), and the Service Rating mode is read as
// "enabled" only when it is exactly 'customer_display' -- the value the rest of the app (evaluationSession.js) treats as on.
import { createHelperSync } from '@/services/helperSync';

const LOGIN_ROUTE_NAMES = ['login', 'loginbackoffice'];
// The login page also answers on '/' (an unnamed route), and a back-office login on its own path.
const LOGIN_PATHS = ['/', '/login', '/loginbackoffice'];
let installed = null;

export function readSnapshot(store, router, storage) {
  const route = router && router.currentRoute;
  // vue-router's initial route (START: path '/', nothing matched) is only a placeholder until the first navigation settles: it is
  // not the login page, and nothing is known about the page yet. The real login page matches a route.
  const matched = !!(route && route.matched && route.matched.length > 0);
  const onLogin = !!(route && (LOGIN_ROUTE_NAMES.indexOf(route.name) !== -1 || (matched && LOGIN_PATHS.indexOf(route.path) !== -1)));
  let token = null;
  try { token = storage.getItem('access_token'); } catch (e) { token = null; }
  const authenticated = !!token && !onLogin;
  // The store is always read (even when signed out) so the store watcher keeps depending on it: it must fire when the branch
  // list loads right after a sign-in, which is exactly when the answer changes.
  const state = store.state || {};
  const branchId = state.selected_branch && state.selected_branch._id ? String(state.selected_branch._id) : '';
  const branches = (state.list && state.list.branchs) || [];
  const branch = branches.find((b) => b && b._id === branchId);
  const setting = branch && branch.setting;
  const enabled = setting && typeof setting === 'object' ? setting.service_rating_mode === 'customer_display' : null;
  // The standalone pages (the Customer Display, the button test page) are kiosk windows, not branch pages: they say nothing, so a
  // display window can never speak for -- or against -- the signed-in branch of the user's own tab.
  if (route && route.meta && route.meta.standalone) return { authenticated: null, branchId: '', enabled: null };
  // Token present but the first navigation has not settled: unknown, not signed out (a tab that is still starting up must not
  // announce a sign-out). No token at all is a truthful sign-out whatever the route.
  if (token && route && !matched && !onLogin) return { authenticated: null, branchId: '', enabled: null };
  if (!authenticated) return { authenticated: false, branchId: '', enabled: null };
  return { authenticated: true, branchId, enabled };
}

// Idempotent: calling it again (hot reload, tests) returns the running instance and never starts a second connection.
export function installHelperSync({ store, router, win, storage, createSync } = {}) {
  if (installed) return installed;
  const w = win || window;
  const st = storage || w.localStorage;
  const sync = (createSync || createHelperSync)({ win: w, getSnapshot: () => readSnapshot(store, router, st) });
  const notify = () => sync.notify();

  const unwatch = store.watch(
    () => {
      const s = readSnapshot(store, router, st);
      return [s.authenticated, s.branchId, s.enabled].join('|');
    },
    notify
  );
  const unhook = router.afterEach(() => {
    notify();
    // Sign-out removes the token while the login page is being created, i.e. just after the navigation settles.
    w.setTimeout(notify, 50);
  });
  const onStorage = (e) => {
    if (!e || e.key === null || e.key === 'access_token') notify();
  };
  w.addEventListener('storage', onStorage);

  installed = {
    sync,
    uninstall() {
      unwatch();
      if (typeof unhook === 'function') unhook();
      w.removeEventListener('storage', onStorage);
      sync.stop();
      installed = null;
    }
  };
  return installed;
}
